/**
 * Game clock — the single home for all in-world time derivation.
 *
 * Time is one monotonic integer, `_clock` = in-world minutes elapsed since the
 * game's start epoch, held as an ordinary blackboard variable (so it rides the
 * existing snapshot/restore paths for rollback and save/load). Every hour /
 * minute / weekday / phase value is *derived* here — no other module does
 * minute math. A missing `_clock` reads as 0, so a new game starts at the
 * epoch with no explicit initialization.
 *
 * See design_docs/game_time_design_10JUN26.md.
 */

import { Blackboard, tagValue } from "./Blackboard";

export interface DayPhase {
  name: string;
  /** Hour (0..hours_per_day-1) at which this phase begins. */
  from: number;
}

export interface CalendarConfig {
  minutes_per_hour: number;
  hours_per_day: number;
  weekdays: string[];
  start: { day: number; weekday: number; hour: number; minute: number };
  label: { month: string; year: string };
  /** Catch-up granularity for advance(), in minutes. */
  time_step: number;
  day_phases: DayPhase[];
  display: { clock: "24h" | "12h"; format: string };
  /** Default minutes charged for a sandbox exit that omits its own time_cost (v2). */
  exit_cost: number;
  /** Default minutes charged on a `-> MENU` narrative→sandbox transition (v2). */
  menu_cost: number;
  /** Time the built-in `sleep` verb wakes at; defaults to start.hour/minute (v4). */
  wake_hour: number;
  wake_minute: number;
}

/** Safety bound on the catch-up loop — mirrors RuleEngine's MAX_CASCADE_DEPTH. */
export const MAX_STEPS = 100000;

const CLOCK_VAR = "_clock";

const DEFAULT_CALENDAR: CalendarConfig = {
  minutes_per_hour: 60,
  hours_per_day: 24,
  weekdays: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
  start: { day: 1, weekday: 0, hour: 8, minute: 0 },
  label: { month: "", year: "" },
  time_step: 15,
  day_phases: [
    { name: "morning", from: 5 },
    { name: "afternoon", from: 12 },
    { name: "evening", from: 17 },
    { name: "night", from: 21 },
  ],
  display: { clock: "24h", format: "{weekday} {hour}:{minute}" },
  exit_cost: 0,
  menu_cost: 0,
  wake_hour: 8,
  wake_minute: 0,
};

/**
 * Build a complete CalendarConfig from a partial `meta.calendar`, filling
 * defaults for every omitted field. Phases are sorted by start hour.
 */
export function buildCalendarConfig(partial?: Partial<CalendarConfig> | null): CalendarConfig {
  const p = partial ?? {};
  const phases = (p.day_phases && p.day_phases.length > 0 ? p.day_phases : DEFAULT_CALENDAR.day_phases)
    .slice()
    .sort((a, b) => a.from - b.from);
  const start = { ...DEFAULT_CALENDAR.start, ...(p.start ?? {}) };
  return {
    minutes_per_hour: p.minutes_per_hour ?? DEFAULT_CALENDAR.minutes_per_hour,
    hours_per_day: p.hours_per_day ?? DEFAULT_CALENDAR.hours_per_day,
    weekdays: p.weekdays && p.weekdays.length > 0 ? p.weekdays : DEFAULT_CALENDAR.weekdays,
    start,
    label: { ...DEFAULT_CALENDAR.label, ...(p.label ?? {}) },
    time_step: p.time_step && p.time_step >= 1 ? p.time_step : DEFAULT_CALENDAR.time_step,
    day_phases: phases,
    display: { ...DEFAULT_CALENDAR.display, ...(p.display ?? {}) },
    exit_cost: p.exit_cost && p.exit_cost > 0 ? p.exit_cost : DEFAULT_CALENDAR.exit_cost,
    menu_cost: p.menu_cost && p.menu_cost > 0 ? p.menu_cost : DEFAULT_CALENDAR.menu_cost,
    // Wake time defaults to the day's start (override for night-shift games).
    wake_hour: p.wake_hour ?? start.hour,
    wake_minute: p.wake_minute ?? start.minute,
  };
}

export class GameClock {
  constructor(
    private bb: Blackboard,
    readonly cfg: CalendarConfig,
    /** Invoked once after each non-empty advance() — the HUD "time.changed" hook. */
    private onChange?: () => void,
  ) {}

  // --- internal derivation ---

  /** Raw `_clock` value (minutes since epoch); missing reads as 0. */
  private rawMinutes(): number {
    const tv = this.bb.get(CLOCK_VAR);
    return tv.type === "num" ? Math.trunc(tv.v) : 0;
  }

  private get minutesPerDay(): number {
    return this.cfg.minutes_per_hour * this.cfg.hours_per_day;
  }

  /** Minutes since epoch, with the start-of-day offset folded in. */
  private absMinute(): number {
    return this.rawMinutes() + this.cfg.start.hour * this.cfg.minutes_per_hour + this.cfg.start.minute;
  }

  /** Whole days elapsed since the start day. */
  private daysElapsed(): number {
    return Math.floor(this.absMinute() / this.minutesPerDay);
  }

  /** Minute within the current day, 0..minutesPerDay-1. */
  private minuteOfDay(): number {
    const m = this.absMinute() % this.minutesPerDay;
    return m < 0 ? m + this.minutesPerDay : m;
  }

  // --- accessors ---

  clock(): number {
    return this.rawMinutes();
  }

  hour(): number {
    return Math.floor(this.minuteOfDay() / this.cfg.minutes_per_hour);
  }

  minute(): number {
    return this.minuteOfDay() % this.cfg.minutes_per_hour;
  }

  /** 1-based day number (= day-of-month when a game stays within one month). */
  day(): number {
    return this.cfg.start.day + this.daysElapsed();
  }

  /** 0-based index into weekdays[]. */
  dayOfWeek(): number {
    const n = this.cfg.weekdays.length;
    return ((this.cfg.start.weekday + this.daysElapsed()) % n + n) % n;
  }

  /** Weekday name, e.g. "Tuesday". */
  weekday(): string {
    return this.cfg.weekdays[this.dayOfWeek()];
  }

  /** Name of the phase whose window contains the current hour (wraps past the last). */
  partOfDay(): string {
    const h = this.hour();
    const phases = this.cfg.day_phases;
    let current = phases[phases.length - 1].name; // wrap: before the first start = last phase
    for (const phase of phases) {
      if (h >= phase.from) current = phase.name;
    }
    return current;
  }

  /** Formatted current time-of-day, honoring display.clock (24h / 12h). */
  timeStr(): string {
    const h = this.hour();
    const m = this.minute();
    const mm = String(m).padStart(2, "0");
    if (this.cfg.display.clock === "12h") {
      const suffix = h < 12 ? "AM" : "PM";
      const h12 = h % 12 === 0 ? 12 : h % 12;
      return `${h12}:${mm} ${suffix}`;
    }
    return `${String(h).padStart(2, "0")}:${mm}`;
  }

  month(): string {
    return this.cfg.label.month;
  }

  year(): string {
    return this.cfg.label.year;
  }

  /**
   * The HUD readout — `display.format` with its `{token}`s interpolated.
   * Supported tokens: weekday, day_of_week, hour, minute (zero-padded), day,
   * month, year, time / time_str (display-aware HH:MM), part_of_day. Unknown
   * tokens are left as-is.
   */
  formatted(): string {
    const tokens: Record<string, string> = {
      weekday: this.weekday(),
      day_of_week: String(this.dayOfWeek()),
      hour: String(this.hour()),
      minute: String(this.minute()).padStart(2, "0"),
      day: String(this.day()),
      month: this.month(),
      year: this.year(),
      time: this.timeStr(),
      time_str: this.timeStr(),
      part_of_day: this.partOfDay(),
    };
    return this.cfg.display.format.replace(/\{(\w+)\}/g, (m, k) => (k in tokens ? tokens[k] : m));
  }

  // --- tests (logical predicates) ---

  before(h: number, m: number): boolean {
    return this.minuteOfDay() < h * this.cfg.minutes_per_hour + m;
  }

  after(h: number, m: number): boolean {
    return this.minuteOfDay() > h * this.cfg.minutes_per_hour + m;
  }

  /** Inclusive time-of-day window; handles windows that wrap past midnight. */
  between(h1: number, m1: number, h2: number, m2: number): boolean {
    const cur = this.minuteOfDay();
    const start = h1 * this.cfg.minutes_per_hour + m1;
    const end = h2 * this.cfg.minutes_per_hour + m2;
    if (start <= end) return cur >= start && cur <= end;
    return cur >= start || cur <= end; // wraps midnight
  }

  /** Today is the given weekday (by 0-based index or by name, case-insensitive). */
  isWeekday(nameOrIdx: number | string): boolean {
    if (typeof nameOrIdx === "number") return this.dayOfWeek() === nameOrIdx;
    return this.weekday().toLowerCase() === nameOrIdx.toLowerCase();
  }

  /** Clock is at or past the absolute moment (day `d`, `h:m`) — the deadline test. */
  onOrAfter(d: number, h: number, m: number): boolean {
    const target =
      (d - this.cfg.start.day) * this.minutesPerDay + h * this.cfg.minutes_per_hour + m;
    return this.absMinute() >= target;
  }

  // --- quantitative reader ---

  /** Minutes from now to the next occurrence of `h:m` (0 if it is exactly now). */
  minutesUntil(h: number, m: number): number {
    const target = h * this.cfg.minutes_per_hour + m;
    const cur = this.minuteOfDay();
    const mpd = this.minutesPerDay;
    return ((target - cur) % mpd + mpd) % mpd;
  }

  // --- mutation ---

  /**
   * Advance the clock forward by `minutes`, stepping in `time_step` increments
   * so that any `_clock`-triggered rule fires at each boundary (catch-up, §5).
   * `onStep` is invoked after each step with the new raw clock value. Negative
   * or zero durations are a no-op (time is forward-only).
   */
  advance(minutes: number, onStep?: (clock: number) => void): void {
    if (minutes <= 0) return;
    let remaining = Math.trunc(minutes);
    let cur = this.rawMinutes();
    let guard = 0;
    const step = this.cfg.time_step;
    while (remaining > 0) {
      if (++guard > MAX_STEPS) {
        console.warn(`GameClock: advance exceeded MAX_STEPS (${MAX_STEPS}) — stopping`);
        break;
      }
      const inc = Math.min(step, remaining);
      cur += inc;
      remaining -= inc;
      this.bb.set(CLOCK_VAR, tagValue(cur));
      onStep?.(cur);
    }
    // One HUD update after the whole jump — no per-step churn (design §5).
    this.onChange?.();
  }
}
